
import React, { lazy, Suspense, useEffect, useState } from 'react';

import {
  DeviceProfile,
  InOut,
} from '../../model/model';

import { Form, Input, Button, Tabs, Spin, Select, InputNumber } from 'antd';

import { onFinishFailed } from '../helpers';
import SessionStore from '../../model/SessionStore';

// Lazily load the TTN profile config form so it is only bundled when needed.
const TtnProfConfForm = lazy(
  () => import('../../plug/io/ttn/ProfConfForm'),
);

interface IProps {
  initialValues: DeviceProfile;
  onFinish: (obj: DeviceProfile) => void;
  update?: boolean;
}

/**
 * Parses the `config` JSON string stored on a {@link DeviceProfile} into an object.
 * Returns an empty object on any parse error.
 */
function parseConfig(config: string | undefined | null): Record<string, unknown> {
  if (!config) return {};
  try {
    return JSON.parse(config);
  } catch {
    return {};
  }
}

function DeviceProfileForm(props: IProps) {
  const [ioType, setIoType] = useState<string>('');
  const [inOutList, setInOutList] = useState<InOut[]>([]);
  const [configObj, setConfigObj] = useState<Record<string, unknown>>(
    parseConfig(props.initialValues.config),
  );

  // Load all InOut entries for the select dropdown.
  useEffect(() => {
    SessionStore.getClient()
      .getAllInOut(SessionStore.getBearer())
      .then((resp) => setInOutList(resp.data ?? []))
      .catch(() => setInOutList([]));
  }, []);

  // Whenever ioId changes (or on mount), resolve the InOut type.
  useEffect(() => {
    const ioId = props.initialValues.ioId;
    if (!ioId) {
      setIoType('');
      return;
    }
    SessionStore.getClient()
      .getInOut(ioId, SessionStore.getBearer())
      .then((resp) => {
        const io: InOut = resp.data;
        setIoType(io?.type ?? '');
      })
      .catch(() => setIoType(''));
  }, [props.initialValues.ioId]);

  const onValuesChange = (changed: Partial<DeviceProfile>) => {
    if ('ioId' in changed) {
      const selectedId = changed.ioId ?? '';
      const found = inOutList.find((io) => io.id === selectedId);
      setIoType(found?.type ?? '');
    }
  };

  const onFinish = (values: DeviceProfile) => {
    const v = Object.assign(props.initialValues, values);
    // Serialise the config object back to a JSON string before submitting.
    v.config = JSON.stringify(configObj);
    props.onFinish(v);
  };

  return (
    <Form
      layout="vertical"
      initialValues={props.initialValues}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      onValuesChange={onValuesChange}
    >
      <Tabs>
        <Tabs.TabPane tab="General" key="1">
          <Form.Item label="Id" name="id" rules={[{ required: true, message: 'Please enter a ID!' }]}>
            <Input disabled={props.update} />
          </Form.Item>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please enter a name!' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="IO" name="ioId">
            <Select allowClear placeholder="Select InOut">
              {inOutList.map((io) => (
                <Select.Option key={io.id} value={io.id}>
                  {io.name} ({io.type})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Vendor" name="vendor">
            <Input.TextArea />
          </Form.Item>
          <Form.Item label="Verison" name="version">
            <Input.TextArea />
          </Form.Item>
          <Form.Item label="Coils Count" name="coilsCount">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Tabs.TabPane>

        {ioType === 'TTN' && (
          <Tabs.TabPane tab="TTN Profile Config" key="ttn-prof">
            <Suspense fallback={<Spin />}>
              <TtnProfConfForm
                value={configObj as any}
                onChange={(cfg) => setConfigObj(cfg as unknown as Record<string, unknown>)}
              />
            </Suspense>
          </Tabs.TabPane>
        )}
      </Tabs>
      <Form.Item>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </Form>
  );
}

export default DeviceProfileForm;
